// §11.14 (D-335) — Alarms & Stopwatch.
//
// **The app owns none of the time.** The schedule is a kernel service (`kernel/alarms`), because an
// alarm that stops existing when its window closes is a countdown you have to babysit. What this
// file owns is the *display* clock — how often the digits move — which is a rendering decision the
// schedule has no opinion about, and which differs by an order of magnitude between the two tabs:
// a list of alarms is honest at one second, and a stopwatch showing hundredths is not.
//
// Everything it can do arrives as an `AlarmShellAccess` (D-059); it reaches no kernel service and
// holds no timer that outlives the window (§6.5).
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  DAY_NAMES,
  WEEKDAYS,
  describeDays,
  elapsedOf,
  formatStopwatch,
  formatTimeOfDay,
  lapSplits,
  parseTimeOfDay,
  ringAt,
  type Alarm,
  type AlarmShellAccess,
  type Stopwatch,
  type Weekday,
} from '@hangar/shared';
import { COPY } from '@/copy';
import './alarms.css';

/** The list's clock. One second is the coarsest tick that keeps "next in 3 min" honest. */
const LIST_TICK_MS = 1_000;
/**
 * The stopwatch's.
 *
 * Fast enough that the hundredths look continuous, and deliberately **not** per animation frame:
 * a running stopwatch would then hold a frame every 16 ms for as long as it is open, which is the
 * cost D-255 refuses for a paused game and there is no reason to pay it here either.
 */
const WATCH_TICK_MS = 47;

const clockOf = (at: number): string =>
  new Date(at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

export interface AlarmsProps {
  access?: AlarmShellAccess;
}

export function Alarms({ access }: AlarmsProps) {
  const [tab, setTab] = useState<'alarms' | 'stopwatch'>('alarms');
  // A counter rather than the snapshot itself: the service owns the data, and copying it into
  // state here would be a second source of truth that could disagree with the one that rings.
  const [, bump] = useState(0);
  const [now, setNow] = useState(() => Date.now());

  const [time, setTime] = useState('');
  const [label, setLabel] = useState('');
  const [days, setDays] = useState<readonly Weekday[]>([]);

  useEffect(() => {
    if (access === undefined) return;
    return access.subscribe(() => bump((n) => n + 1));
  }, [access]);

  // A snapshot of the shell's schedule, or the empty one — an app mounted without its capability
  // draws an empty list rather than throwing, which is the same shape every other built-in takes.
  const snapshot: {
    alarms: readonly Alarm[];
    stopwatch: Stopwatch | null;
    ringing: readonly string[];
  } = access?.snapshot() ?? { alarms: [], stopwatch: null, ringing: [] };
  const watch = snapshot.stopwatch;
  const running = watch?.running ?? false;

  // One interval, at whichever cadence the visible tab needs. Switching tabs re-arms it, which is
  // why the rate is derived rather than held.
  useEffect(() => {
    const every = tab === 'stopwatch' && running ? WATCH_TICK_MS : LIST_TICK_MS;
    const handle = globalThis.setInterval(() => setNow(Date.now()), every);
    return () => globalThis.clearInterval(handle);
  }, [tab, running]);

  const parsed = useMemo(() => parseTimeOfDay(time), [time]);

  const add = useCallback(() => {
    if (access === undefined || parsed === null) return;
    access.add({
      label: label.trim().length > 0 ? label.trim() : COPY.alarms.labelPlaceholder,
      hour: parsed.hour,
      minute: parsed.minute,
      days,
    });
    setTime('');
    setLabel('');
    setDays([]);
  }, [access, parsed, label, days]);

  const remove = useCallback(
    async (alarm: Alarm) => {
      if (access === undefined) return;
      // §19.4 — through the shell. An alarm is a promise about a future morning, and deleting one
      // silently is how somebody finds out they no longer have it at 7:31.
      if (!(await access.confirm(COPY.alarms.confirmDelete(alarm.label)))) return;
      access.remove(alarm.id);
    },
    [access],
  );

  const ringing = useMemo(
    () => snapshot.alarms.filter((alarm) => snapshot.ringing.includes(alarm.id)),
    [snapshot],
  );

  /** What a row says under its label: the next ring, a snooze, or that it is off. */
  const statusOf = (alarm: Alarm): string => {
    if (alarm.snoozedUntil !== null) return COPY.alarms.snoozedUntil(clockOf(alarm.snoozedUntil));
    const at = ringAt(alarm);
    if (at !== null) return COPY.alarms.nextRing(clockOf(at));
    if (alarm.lastRangAt !== null) return COPY.alarms.rangAt(clockOf(alarm.lastRangAt));
    return COPY.alarms.notArmed;
  };

  const elapsed = watch === null ? 0 : elapsedOf(watch, now);
  const splits = useMemo(() => lapSplits(watch?.laps ?? []), [watch?.laps]);

  return (
    <div className="alarms" data-testid="alarms">
      <header className="alarms-tabs" role="tablist" aria-label={COPY.alarms.title}>
        {(['alarms', 'stopwatch'] as const).map((which) => (
          <button
            key={which}
            type="button"
            role="tab"
            className="alarms-tab"
            data-testid={`alarms-tab-${which}`}
            aria-selected={tab === which}
            onClick={() => setTab(which)}
          >
            {which === 'alarms' ? COPY.alarms.tabAlarms : COPY.alarms.tabStopwatch}
          </button>
        ))}
      </header>

      {/* A ring is drawn wherever you are, including on the stopwatch tab: it is the one thing in
          this app that is time-critical, and hiding it behind a tab would be the app failing at
          the only job it has. */}
      {ringing.length > 0 ? (
        <section className="alarms-ringing" data-testid="alarms-ringing" role="alert">
          {ringing.map((alarm) => (
            <div className="alarms-ring" key={alarm.id} data-testid={`alarms-ring-${alarm.id}`}>
              <span className="alarms-ring-label">{COPY.alarms.ringingTitle(alarm.label)}</span>
              <span className="alarms-ring-time">
                {COPY.alarms.ringingBody(alarm.hour, alarm.minute)}
              </span>
              <button
                type="button"
                data-testid={`alarms-snooze-${alarm.id}`}
                onClick={() => access?.snooze(alarm.id)}
              >
                {COPY.alarms.snooze}
              </button>
              <button
                type="button"
                data-testid={`alarms-dismiss-${alarm.id}`}
                onClick={() => access?.dismiss(alarm.id)}
              >
                {COPY.alarms.dismiss}
              </button>
            </div>
          ))}
        </section>
      ) : null}

      {tab === 'alarms' ? (
        <section className="alarms-pane" data-testid="alarms-pane">
          <form
            className="alarms-form"
            onSubmit={(event) => {
              event.preventDefault();
              add();
            }}
          >
            <label className="alarms-field">
              <span>{COPY.alarms.timeLabel}</span>
              <input
                data-testid="alarms-time"
                value={time}
                placeholder={COPY.alarms.timePlaceholder}
                onChange={(event) => setTime(event.currentTarget.value)}
              />
            </label>
            <label className="alarms-field">
              <span>{COPY.alarms.labelLabel}</span>
              <input
                data-testid="alarms-label"
                value={label}
                placeholder={COPY.alarms.labelPlaceholder}
                onChange={(event) => setLabel(event.currentTarget.value)}
              />
            </label>
            <fieldset className="alarms-days">
              <legend>{COPY.alarms.repeatLabel}</legend>
              {WEEKDAYS.map((day) => (
                <button
                  key={day}
                  type="button"
                  className="alarms-day"
                  data-testid={`alarms-day-${day}`}
                  aria-pressed={days.includes(day)}
                  onClick={() =>
                    setDays((current) =>
                      current.includes(day)
                        ? current.filter((one) => one !== day)
                        : [...current, day].sort(),
                    )
                  }
                >
                  {DAY_NAMES[day]}
                </button>
              ))}
            </fieldset>
            <button type="submit" data-testid="alarms-add" disabled={parsed === null}>
              {COPY.alarms.add}
            </button>
          </form>
          <p className="alarms-hint">{COPY.alarms.timeHint}</p>

          {snapshot.alarms.length === 0 ? (
            <p className="alarms-empty" data-testid="alarms-empty">
              {COPY.alarms.noAlarms}
            </p>
          ) : (
            <ul className="alarms-list" data-testid="alarms-list">
              {snapshot.alarms.map((alarm) => (
                <li className="alarms-row" key={alarm.id} data-testid={`alarms-row-${alarm.id}`}>
                  <span className="alarms-clock" data-testid={`alarms-at-${alarm.id}`}>
                    {formatTimeOfDay(alarm.hour, alarm.minute)}
                  </span>
                  <span className="alarms-detail">
                    <span className="alarms-name">{alarm.label}</span>
                    <span className="alarms-status" data-testid={`alarms-status-${alarm.id}`}>
                      {describeDays(alarm.days)} · {statusOf(alarm)}
                    </span>
                  </span>
                  <label className="alarms-switch">
                    <input
                      type="checkbox"
                      data-testid={`alarms-enabled-${alarm.id}`}
                      aria-label={COPY.alarms.enabledLabel(alarm.label)}
                      checked={alarm.enabled}
                      onChange={(event) => access?.toggle(alarm.id, event.currentTarget.checked)}
                    />
                  </label>
                  <button
                    type="button"
                    data-testid={`alarms-remove-${alarm.id}`}
                    onClick={() => void remove(alarm)}
                  >
                    {COPY.alarms.remove}
                  </button>
                </li>
              ))}
            </ul>
          )}

          {/* The limitation, said out loud. An alarm app that let somebody believe it rings with
              the tab shut would be worse than none — they find out on the morning it mattered. */}
          <p className="alarms-note" data-testid="alarms-note">
            {COPY.alarms.closedTabNote}
          </p>
        </section>
      ) : (
        <section className="alarms-pane alarms-watch" data-testid="alarms-stopwatch">
          <p className="alarms-elapsed" data-testid="alarms-elapsed">
            {formatStopwatch(elapsed)}
          </p>
          <div className="alarms-watch-actions">
            <button
              type="button"
              data-testid="alarms-watch-toggle"
              onClick={() => (running ? access?.stopStopwatch() : access?.startStopwatch())}
            >
              {running ? COPY.alarms.stop : elapsed > 0 ? COPY.alarms.resume : COPY.alarms.start}
            </button>
            <button
              type="button"
              data-testid="alarms-watch-lap"
              disabled={!running}
              onClick={() => access?.lapStopwatch()}
            >
              {COPY.alarms.lap}
            </button>
            <button
              type="button"
              data-testid="alarms-watch-reset"
              disabled={running || elapsed === 0}
              onClick={() => access?.resetStopwatch()}
            >
              {COPY.alarms.reset}
            </button>
          </div>

          {splits.length === 0 ? (
            <p className="alarms-empty" data-testid="alarms-no-laps">
              {COPY.alarms.noLaps}
            </p>
          ) : (
            <ol className="alarms-laps" data-testid="alarms-laps">
              {/* Newest first, because the lap you just took is the one you are looking for. */}
              {[...splits].reverse().map((split, index) => {
                const number = splits.length - index;
                return (
                  <li className="alarms-lap" key={number} data-testid={`alarms-lap-${number}`}>
                    <span>{COPY.alarms.lapNumber(number)}</span>
                    <span className="alarms-lap-split">{formatStopwatch(split)}</span>
                    <span className="alarms-lap-total">
                      {formatStopwatch(watch?.laps[number - 1] ?? 0)}
                    </span>
                  </li>
                );
              })}
            </ol>
          )}
        </section>
      )}
    </div>
  );
}

export default Alarms;
